/**
 * PROJECT PRICING (owner rulings 2026-10-27, "Pricing Sheets - 2027 Events").
 *
 * KEEP IN STEP WITH emerge-backend/src/lib/project-pricing.ts (same file, same
 * tests in spirit). Pure: no DB, no Date, money as decimal strings via BigInt.
 *
 * A price entry is per project x product x CURRENCY (USD / GBP / EUR; the
 * document currency picks it) x TIER (Standard / Exclusive / Early bird, a
 * master `f_price_tiers` with a default flag; never decided by name).
 *
 *   per_city_rate  set -> unit price = rate x cities on the line, unless an
 *                  override exists for that exact city count (packages such as
 *                  the Africa travel "without Lagos and Abuja" = 4 cities, or a
 *                  rounded EUR exclusive). Cities = all project cities when the
 *                  line is flagged all-cities or selects none.
 *   flat_price     (per_city_rate null) -> unit price = flat price, unless an
 *                  override exists for the line QUANTITY (Masterclasses: 2
 *                  sessions = 12,000 in total). A quantity override is a LINE
 *                  TOTAL: unit price = total / quantity; when that is not whole
 *                  cents the unit price rounds up and the difference becomes a
 *                  discount amount, so the line total is exactly the card rate.
 *
 * Missing tier -> the default (Standard) tier. No entry at all -> null, and the
 * caller falls back to the legacy catalogue rule (city-pro-rata.ts).
 * Typed prices are never overwritten; that is the caller's rule.
 */

export interface ProjectPriceOverride {
  /** Cities on the line (per-city entries) or line quantity (flat entries). */
  unitCount: number;
  /** Decimal string. Per-city: the unit price. Flat: the LINE TOTAL. */
  price: string;
}

export interface ProjectPriceEntry {
  productId: number;
  currency: string;
  tierId: number;
  perCityRate: string | null;
  flatPrice: string | null;
  overrides: ProjectPriceOverride[];
}

export interface ResolvePriceInput {
  entries: readonly ProjectPriceEntry[];
  productId: number;
  currency: string;
  /** The line's tier; null = default tier. */
  tierId: number | null;
  defaultTierId: number;
  selectedCityCount: number;
  projectCityCount: number;
  allProjectCities?: boolean;
  quantity: number;
}

export type PriceBasis = 'per_city' | 'city_override' | 'flat' | 'quantity_override';

export interface ResolvedPrice {
  unitPrice: string;
  /** Non-null only when a quantity override does not divide into whole cents. */
  discountAmount: string | null;
  /** The tier actually used (the default when the requested tier has no entry). */
  tierId: number;
  basis: PriceBasis;
}

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;

function toCents(value: string): bigint {
  const match = DECIMAL.exec(value.trim());
  if (!match) throw new TypeError(`project-pricing: not a decimal string: ${JSON.stringify(value)}`);
  const [, sign, whole = '0', fraction = ''] = match;
  const frac = (fraction + '00').slice(0, 2);
  const rest = fraction.slice(2);
  let cents = BigInt(whole) * 100n + BigInt(frac);
  if (rest.length > 0 && BigInt(rest) > 0n) {
    // More than 2 decimals: round half up on the third digit.
    if (Number(rest[0]) >= 5) cents += 1n;
  }
  return sign === '-' ? -cents : cents;
}

function formatCents(cents: bigint): string {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  return `${negative && abs !== 0n ? '-' : ''}${abs / 100n}.${(abs % 100n).toString().padStart(2, '0')}`;
}

function assertCount(value: number, field: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`project-pricing: ${field} must be a non-negative integer, got ${String(value)}`);
  }
}

function pickEntry(input: ResolvePriceInput, tierId: number): ProjectPriceEntry | undefined {
  const currency = input.currency.trim().toUpperCase();
  return input.entries.find(
    (e) => e.productId === input.productId && e.currency.toUpperCase() === currency && e.tierId === tierId,
  );
}

export function resolveProjectPrice(input: ResolvePriceInput): ResolvedPrice | null {
  assertCount(input.selectedCityCount, 'selectedCityCount');
  assertCount(input.projectCityCount, 'projectCityCount');
  assertCount(input.quantity, 'quantity');

  const requested = input.tierId ?? input.defaultTierId;
  let entry = pickEntry(input, requested);
  let tierId = requested;
  if (entry === undefined && requested !== input.defaultTierId) {
    entry = pickEntry(input, input.defaultTierId);
    tierId = input.defaultTierId;
  }
  if (entry === undefined) return null;

  if (entry.perCityRate !== null) {
    const cities =
      input.allProjectCities === true || input.selectedCityCount === 0
        ? input.projectCityCount
        : input.selectedCityCount;
    const override = entry.overrides.find((o) => o.unitCount === cities);
    if (override !== undefined) {
      return { unitPrice: formatCents(toCents(override.price)), discountAmount: null, tierId, basis: 'city_override' };
    }
    if (cities === 0) {
      if (entry.flatPrice === null) return null;
      return { unitPrice: formatCents(toCents(entry.flatPrice)), discountAmount: null, tierId, basis: 'flat' };
    }
    return {
      unitPrice: formatCents(toCents(entry.perCityRate) * BigInt(cities)),
      discountAmount: null,
      tierId,
      basis: 'per_city',
    };
  }

  const quantity = input.quantity === 0 ? 1 : input.quantity;
  const override = entry.overrides.find((o) => o.unitCount === quantity);
  if (override !== undefined) {
    const total = toCents(override.price);
    const q = BigInt(quantity);
    const unit = (total + q - 1n) / q; // round up to whole cents
    const discount = unit * q - total;
    return {
      unitPrice: formatCents(unit),
      discountAmount: discount === 0n ? null : formatCents(discount),
      tierId,
      basis: 'quantity_override',
    };
  }
  if (entry.flatPrice === null) return null;
  return { unitPrice: formatCents(toCents(entry.flatPrice)), discountAmount: null, tierId, basis: 'flat' };
}

/** Tiers that have an entry for this product and currency (for the line picker). */
export function pricedTierIds(
  entries: readonly ProjectPriceEntry[],
  productId: number,
  currency: string,
): number[] {
  const c = currency.trim().toUpperCase();
  return [...new Set(entries.filter((e) => e.productId === productId && e.currency.toUpperCase() === c).map((e) => e.tierId))];
}
